import { useAtom } from 'jotai';
import { ArrowDownToLine, ArrowUpFromLine, GitBranch, Layers, Waves, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// X icon retained: still used by the per-pond watershed-filter clear
// button below. Inspector-related imports trimmed when the feature
// view moved into FeatureProperties.

import {
  rainfallAccumulationThresholdAtom,
  rainfallSelectedPondIdAtom,
  rainfallShowOverflowArrowsAtom,
  rainfallVisibleKindsAtom,
  type RainfallVisibleKind,
} from '@/atoms/rainfall.atom';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';

interface Props {
  /** Per-run flow-accumulation summary (read off the VECTOR layer's
   *  metadata in the parent panel and forwarded for the slider's
   *  upper bound). The panel only needs `maxFlowAccumulationM2`. */
  summary?: { maxFlowAccumulationM2?: number };
}

/**
 * The slider value is stored as a real m² number (rainfallAccumulationThresholdAtom)
 * but the slider itself works on log10 of that value. Converting at the
 * boundary keeps the rest of the system thinking in m² of upstream area
 * (which is what the worker emits and what the planner thinks in) while
 * the slider gives a pleasant log-scale feel — moving the thumb a quarter
 * across goes from 100 → 10,000 m², which is the actual range planners
 * want to scrub through.
 */
const LOG_FLOOR = Math.log10(50); // worker emits everything above 50 m²
const LOG_CEIL_FALLBACK = Math.log10(1_000_000); // 1 km² catchment

/**
 * Locale-aware compact integer formatting for stat readouts. We keep
 * hundreds; thousands collapse to "1.2k", millions to "3.4M". Helps
 * the panel stay readable on a busy site.
 */
function fmtCompact(n: number | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toFixed(0);
}

/**
 * Inline controls embedded inside `RainfallSummaryPanel`'s expanded
 * section (one panel per rainfall result, dismissed together). Surfaces:
 *   - Per-kind toggles for the VECTOR child (flow lines / ponds / watersheds).
 *     These drive client-side filters via Jotai atoms — no server round-trip.
 *   - Overflow-arrow visibility toggle (always-on annotation between
 *     connected pond centroids).
 *   - Log-scale slider for the flow-line accumulation threshold.
 *   - "Show watersheds for selected pond only" toggle (gated on a pond
 *     being selected — clicking a pond enables it).
 *   - Inspector pane with kind-specific stats for the selected feature,
 *     including storm-mode metrics (peak runoff, fill time, inflow,
 *     overflow chain position, terminal-pond callout) when present.
 *
 * UX: the raster toggle persists (it's a server-side layer flag the user
 * sees in the layer tree); kind toggles + slider are session-only because
 * they're rapid-iteration controls a planner uses while exploring. Same
 * model as the deviation colourbar / volume panel.
 */
export function RainfallControlsPanel({ summary }: Props) {
  const { t } = useTranslation('project');

  const [visibleKinds, setVisibleKinds] = useAtom(rainfallVisibleKindsAtom);
  const [threshold, setThreshold] = useAtom(rainfallAccumulationThresholdAtom);
  const [selectedPondId, setSelectedPondId] = useAtom(rainfallSelectedPondIdAtom);
  const [showOverflowArrows, setShowOverflowArrows] = useAtom(rainfallShowOverflowArrowsAtom);

  // Slider domain: floor at the worker's emit threshold (50 m²), ceil at
  // the summary's recorded max — fall back to a 1 km² ceiling on
  // legacy-summary jobs that never wrote `maxFlowAccumulationM2`.
  const logCeil = useMemo(() => {
    const max = summary?.maxFlowAccumulationM2;
    if (!max || !Number.isFinite(max) || max <= 50) return LOG_CEIL_FALLBACK;
    return Math.log10(max);
  }, [summary?.maxFlowAccumulationM2]);

  const sliderLog = Math.log10(Math.max(threshold, 50));

  const onKindToggle = (kind: RainfallVisibleKind, next: boolean) => {
    setVisibleKinds({ ...visibleKinds, [kind]: next });
  };

  // No outer container — this is meant to be embedded inside the
  // RainfallSummaryPanel's expanded section, which provides its own
  // background + border. Toggles + slider + inspector stack vertically.
  return (
    <div className='flex flex-col gap-3'>
      {/* Per-kind toggles. */}
      <div className='space-y-1'>
        <KindToggle
          kind='flowLine'
          label={t('rainfallAnalysis.controls.flowLines')}
          icon={<Waves className='h-3.5 w-3.5 text-sky-600' />}
          checked={visibleKinds.flowLine}
          onChange={(v) => onKindToggle('flowLine', v)}
        />
        <KindToggle
          kind='pond'
          label={t('rainfallAnalysis.controls.ponds')}
          icon={<ArrowDownToLine className='h-3.5 w-3.5 text-sky-700' />}
          checked={visibleKinds.pond}
          onChange={(v) => onKindToggle('pond', v)}
        />
        <KindToggle
          kind='watershed'
          label={t('rainfallAnalysis.controls.watersheds')}
          icon={<Layers className='h-3.5 w-3.5 text-slate-500' />}
          checked={visibleKinds.watershed}
          onChange={(v) => onKindToggle('watershed', v)}
        />
        {/* Overflow arrows are independent of any kind toggle — they're
            a derived view of the pond graph, useful even when ponds
            themselves are hidden (e.g. user wants to see chain
            structure without polygon clutter). */}
        <label className='flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-stone-800/40'>
          <span className='flex items-center gap-2'>
            <GitBranch className='h-3.5 w-3.5 text-slate-500' />
            {t('rainfallAnalysis.controls.overflowArrows')}
          </span>
          <Switch checked={showOverflowArrows} onCheckedChange={setShowOverflowArrows} />
        </label>
      </div>

      {/* Flow-line accumulation slider. Disabled when flow lines are
          hidden — a disabled slider reads more clearly than a live one
          driving an invisible layer. */}
      <div className={visibleKinds.flowLine ? '' : 'opacity-50'}>
        <div className='mb-1 flex items-baseline justify-between text-[11px]'>
          <span className='text-slate-700 dark:text-stone-300'>
            {t('rainfallAnalysis.controls.thresholdLabel', {
              value: `${fmtCompact(threshold)} ${t('rainfallAnalysis.controls.thresholdUnit')}`,
            })}
          </span>
        </div>
        <Slider
          value={[sliderLog]}
          min={LOG_FLOOR}
          max={logCeil}
          step={0.05}
          disabled={!visibleKinds.flowLine}
          onValueChange={([v]) => setThreshold(Math.pow(10, v))}
        />
      </div>

      {/* Selected-pond watershed filter. Hidden until a pond is selected
          AND watersheds are visible — surfacing it earlier is just noise. */}
      {selectedPondId != null && visibleKinds.watershed && (
        <div className='flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5 text-[11px] dark:bg-stone-800/50'>
          <div className='flex items-center gap-2'>
            <ArrowUpFromLine className='h-3.5 w-3.5 text-slate-500' />
            {t('rainfallAnalysis.controls.watershedsForSelected')}
          </div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => setSelectedPondId(null)}
            className='h-6 px-2 text-xs'>
            <X className='h-3 w-3' />
          </Button>
        </div>
      )}

      {/* Inspector lives in `FeatureProperties` (right-side drawer)
          now — clicking a rainfall feature opens that drawer with the
          full kind-specific stats via `RainfallFeatureView`. We keep
          the per-pond watershed filter above (it needs the
          `rainfallSelectedPondIdAtom` driven by clicks in GeoJson),
          but the property readout no longer duplicates here. */}
    </div>
  );
}

function KindToggle({
  label,
  icon,
  checked,
  onChange,
}: {
  kind: RainfallVisibleKind;
  label: string;
  icon: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className='flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-stone-800/40'>
      <span className='flex items-center gap-2'>
        {icon}
        {label}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

